/**
 * Admin 财务：入金审核 / 提现审核 API（真联调 + mock 回退）
 */
import { adminGet, adminPost, USE_MOCK } from './client';
import { formatMoney } from '@shared/formats/format';
import { deposits as mockDeposits, withdrawals as mockWithdrawals, type OrigDeposit, type OrigWithdrawal } from '@/mock/origData';

// ---- 后端 DTO（Result.data） ----

export type DepositApiItem = {
  depositId: string;
  depositNo?: string;
  merchantId?: string;
  cardholderId?: string;
  method?: string;
  /** 链上（到账观测）金额；历史单与入账金额一致 */
  amount: string;
  /** 链上（到账观测）币种；历史单为 USDT，新单仍为 USDT */
  currency: string;
  /**
   * 入账币种/金额/折算率（2026-10-29 fund-fix：链上资产与记账币种解耦）。
   * 新单：currency=USDT、creditCurrency=USD、creditAmount=同额、creditRate=1；
   * 历史单：可能为 USDT 或三字段缺失（旧后端），展示层须回退链上口径。
   */
  creditCurrency?: string | null;
  creditAmount?: string | null;
  creditRate?: string | null;
  status: string;
  channelTxId?: string;
  createdAt?: string;
  /**
   * 水单对象键（只读派生自 `deposit_order.address='slip:'+objectKey`）。
   * 非 slip: 前缀的入金单为 null；页面不得从 method 推断对象键。
   */
  slipFileId?: string | null;
};

/** 服务端跨页 KPI 汇总（仅受 merchantId 约束，不受 status/页码影响） */
export type DepositListSummary = {
  pendingCount: number;
  creditedThisMonthCount: number;
  /** 本月已入账金额，按入账币种分组；金额为后端 Money(scale=8) 的 toPlainString 原样 */
  creditedThisMonthTotals: Array<{ currency: string; amount: string }>;
};

export type DepositListResponse = {
  total: number;
  items: DepositApiItem[];
  summary: DepositListSummary;
};

/** 真 API 入金金额只保留服务端 DECIMAL 字符串，禁止降级为 JavaScript number。 */
export type UiDeposit = Omit<OrigDeposit, 'amount'> & {
  amountText: string;
  currencyCode: string;
  /** 入账币种（缺省回退链上币种） */
  creditCurrency?: string;
  /** 入账金额（缺省回退链上金额） */
  creditAmount?: string;
  /** 折算率（十进制字符串，缺省视为 1） */
  creditRate?: string;
};

/** 双币种展示入参（OrigDeposit 缺 amountText/credit* 时由调用方回填链上口径） */
export type DepositCreditInput = {
  amountText?: string;
  currencyCode?: string;
  creditAmount?: string | null;
  creditCurrency?: string | null;
  creditRate?: string | null;
};

export type DepositCreditInfo = {
  /** 链上（到账观测）币种 */
  chainCurrency: string;
  /** 链上（到账观测）金额 */
  chainAmount: string;
  /** 入账币种 */
  creditCurrency: string;
  /** 入账金额 */
  creditAmount: string;
  /** 折算率（十进制字符串，缺省 '1'） */
  creditRate: string;
  /** 是否发生币种折算（链上币种 ≠ 入账币种） */
  converted: boolean;
};

/** 去掉小数尾零（纯字符串处理，不经 Number，避免精度语义损失） */
function trimDecimalZeros(raw: string): string {
  return raw.includes('.') ? raw.replace(/0+$/, '').replace(/\.$/, '') : raw;
}

/**
 * 归一入账口径（2026-10-29 fund-fix / B-6）。
 *
 * 兼容旧后端：`credit*` 三字段缺失时回退为链上 `amount/currency`、汇率 `1`，
 * 因此历史单（creditCurrency=USDT）与新单（creditCurrency=USD）都能得到正确结果。
 */
export function resolveDepositCredit(row: DepositCreditInput): DepositCreditInfo {
  const chainCurrency = String(row.currencyCode ?? '').trim().toUpperCase();
  const chainAmount = String(row.amountText ?? '').trim();
  const creditCurrency = String(row.creditCurrency ?? '').trim().toUpperCase() || chainCurrency;
  const creditAmount = String(row.creditAmount ?? '').trim() || chainAmount;
  const creditRate = String(row.creditRate ?? '').trim() || '1';
  return {
    chainCurrency,
    chainAmount,
    creditCurrency,
    creditAmount,
    creditRate,
    converted: chainCurrency !== '' && creditCurrency !== chainCurrency,
  };
}

/**
 * 链上 → 入账 双币种文案（B-6）：
 *  - 折算单：`链上 USDT 20.00 → 入账 USD 20.00（汇率 1）`
 *  - 同币种（历史单）：`链上 USDT 20.00 → 入账 USDT 20.00`
 *
 * 金额一律经 `formatMoney` 做 2 位屏显（wire 为 8 位 DECIMAL 字符串，展示层裁剪）；
 * 链上或入账金额缺失（旧后端未下发 / 脏数据）返回 null，调用方降级为单金额展示，
 * 保证不渲染 `undefined`。
 */
export function formatDepositCredit(row: DepositCreditInput): string | null {
  const info = resolveDepositCredit(row);
  if (!info.chainAmount || !info.creditAmount) {
    return null;
  }
  const chainText = formatMoney(info.chainAmount, info.chainCurrency);
  const creditText = formatMoney(info.creditAmount, info.creditCurrency);
  const rateText = info.converted ? `（汇率 ${trimDecimalZeros(info.creditRate)}）` : '';
  return `链上 ${chainText} → 入账 ${creditText}${rateText}`;
}

type WithdrawalApiItem = {
  withdrawalId: string;
  subjectType: string;
  amount: string;
  currency: string;
  status: string;
  createdAt?: string;
  userDisplay?: string | null;
  payoutLast4?: string | null;
  fee?: string | null;
  netAmount?: string | null;
};

type WithdrawalListParams = {
  status?: string;
  subjectType?: string;
  page?: number;
  pageSize?: number;
};

// ---- UI 映射 ----

/**
 * 后端 DepositOrderStatus → UI 粗分状态。
 * F-R2：EXCEPTION/IGNORED 不得落入 pending（否则露出通过按钮）。
 */
export function mapDepositStatus(s: string): OrigDeposit['status'] {
  const u = (s || '').toUpperCase();
  if (u === 'CREDITED') return 'approved';
  if (u === 'REJECTED') return 'rejected';
  if (u === 'EXCEPTION') return 'exception';
  if (u === 'IGNORED' || u === 'DUPLICATE_IGNORED' || u === 'LATE_IGNORED') return 'ignored';
  if (
    u === 'PENDING_REVIEW' ||
    u === 'DETECTED' ||
    u === 'CONFIRMING' ||
    u === 'PENDING'
  ) {
    return 'pending';
  }
  // 未知态：不误导为待审
  return 'exception';
}

/** 后端原始状态 → 中文（列表；可与 rawStatus 并用） */
export function formatDepositStatus(raw?: string): string {
  const u = (raw || '').toUpperCase();
  const map: Record<string, string> = {
    DETECTED: '已检测',
    PENDING_REVIEW: '待审核',
    CONFIRMING: '确认中',
    PENDING: '待审核',
    CREDITED: '已通过',
    REJECTED: '已拒绝',
    EXCEPTION: '异常',
    IGNORED: '已忽略',
    DUPLICATE_IGNORED: '已忽略',
    LATE_IGNORED: '已忽略',
  };
  return map[u] || (raw ? raw : '未知');
}

/** 仅开放审状态可点通过/拒绝 */
export function isReviewableDepositStatus(raw?: string): boolean {
  const u = (raw || '').toUpperCase();
  return u === 'PENDING_REVIEW' || u === 'DETECTED' || u === 'CONFIRMING' || u === 'PENDING';
}

function mapDepositType(method?: string): OrigDeposit['type'] {
  const m = (method || '').toUpperCase();
  if (m === 'BANK') return 'bank_slip';
  if (m.includes('TRC') || m === 'CRYPTO') return 'TRC20';
  return 'TRC20';
}

export function toOrigDeposit(item: DepositApiItem): UiDeposit {
  const type = mapDepositType(item.method);
  const raw = item.status || '';
  return {
    id: item.depositId,
    merchantId: item.merchantId ? `M-${item.merchantId}` : 'M-?',
    merchantName: item.merchantId ? `商户 ${item.merchantId}` : '—',
    agentName: '—',
    type,
    amountText: item.amount,
    currencyCode: item.currency,
    // 2026-10-29 fund-fix：入账币种三元组；旧后端无此三字段 → 保持 undefined，展示层回退链上口径
    creditCurrency: item.creditCurrency || undefined,
    creditAmount: item.creditAmount || undefined,
    creditRate: item.creditRate || undefined,
    method:
      type === 'bank_slip'
        ? `银行水单 ${item.depositNo || item.depositId}`
        : `${type} ${item.channelTxId || ''}`.trim(),
    // 时间展示统一：保留原始 ISO，展示层用 formatDateTime
    submittedAt: item.createdAt || '',
    submitter: '系统',
    status: mapDepositStatus(raw),
    rawStatus: raw,
    slipFileId: item.slipFileId || undefined,
  };
}

function mapWithdrawalStatus(s: string): OrigWithdrawal['status'] {
  const u = (s || '').toUpperCase();
  if (u === 'COMPLETED' || u === 'SUCCESS') return 'done';
  if (u === 'REJECTED' || u === 'CANCELLED') return 'rejected';
  if (u === 'BANK_PROCESSING' || u === 'PAYOUT_PROCESSING') return 'paying';
  return 'pending';
}

/** 后端原始状态 → 中文文案（列表展示用，保留 rawStatus 另存） */
export function formatWithdrawalStatus(raw: string): string {
  const u = (raw || '').toUpperCase();
  const map: Record<string, string> = {
    SUBMITTED: '已提交',
    RISK_REVIEW: '风控审核',
    FINANCE_REVIEW: '财务审核',
    BANK_PROCESSING: '银行处理中',
    PAYOUT_PROCESSING: '打款中',
    COMPLETED: '已完成',
    SUCCESS: '已完成',
    REJECTED: '已拒绝',
    CANCELLED: '已取消',
  };
  return map[u] || (raw ? raw : '未知');
}

/** 是否可审（通过/拒绝）：风控 / 财务 / 已提交 */
export function isReviewableWithdrawalStatus(raw?: string): boolean {
  const u = (raw || '').toUpperCase();
  return (
    u === 'RISK_REVIEW' ||
    u === 'FINANCE_REVIEW' ||
    u === 'SUBMITTED'
  );
}

/** 非终态：可展示「7日强驳」 */
export function isForceRejectableWithdrawalStatus(raw?: string): boolean {
  const u = (raw || '').toUpperCase();
  if (!u) return false;
  return u !== 'COMPLETED' && u !== 'SUCCESS' && u !== 'REJECTED' && u !== 'CANCELLED';
}

/** StatusBadge 配色用状态字 */
export function withdrawalBadgeStatus(raw?: string): string {
  const u = (raw || '').toUpperCase();
  if (u === 'COMPLETED' || u === 'SUCCESS') return 'COMPLETED';
  if (u === 'REJECTED' || u === 'CANCELLED') return 'REJECTED';
  if (u === 'BANK_PROCESSING' || u === 'PAYOUT_PROCESSING') return 'PROCESSING';
  return 'PENDING';
}

/**
 * 提现列表行（UI 侧收窄）。
 *
 * 后端 `WithdrawalListItemView` 无「可用余额」、无银行渠道字段，故从
 * `OrigWithdrawal`（`@/mock/origData`，L5 既有 import）中 Omit 掉伪造字段，
 * 金额/手续费/净额一律保留 DECIMAL 字符串，禁止 `Number()` 丢 8 位精度。
 * `types/orig.ts` 与 `mock/origData.ts` 本 Sprint 零 diff（类型收窄只在此处）。
 */
export type UiWithdrawal = Omit<
  OrigWithdrawal,
  'amount' | 'availableBalance' | 'channel' | 'channelType'
> & {
  rawStatus?: string;
  subjectType?: string;
  /** 提现金额（DECIMAL 字符串原样） */
  amountText: string;
  /** 提现币种（后端下发；缺省 USD） */
  currencyCode: string;
  /** 手续费（DECIMAL 字符串；缺失时展示 —） */
  feeText?: string;
  /** 净额（DECIMAL 字符串；缺失时展示 —） */
  netAmountText?: string;
  /** 收款账户后四位（缺失时展示 —，不伪造银行账号） */
  payoutLast4?: string;
};

export function toOrigWithdrawal(item: WithdrawalApiItem): UiWithdrawal {
  return {
    id: item.withdrawalId,
    agentName: item.userDisplay || `${item.subjectType} #${item.withdrawalId}`,
    agentId: item.subjectType || '—',
    contact: item.payoutLast4 ? `****${item.payoutLast4}` : '—',
    // 时间展示统一：保留原始 ISO，展示层用 formatDateTime
    appliedAt: item.createdAt || '',
    status: mapWithdrawalStatus(item.status),
    rawStatus: item.status,
    subjectType: item.subjectType,
    amountText: item.amount,
    currencyCode: item.currency || 'USD',
    feeText: item.fee ?? undefined,
    netAmountText: item.netAmount ?? undefined,
    payoutLast4: item.payoutLast4 ?? undefined,
  };
}

// ---- mock 提现：覆盖状态机样例 ----

const MOCK_SUBJECT_CYCLE = ['AGENT', 'MERCHANT'] as const;
const MOCK_RAW_BY_UI: Record<OrigWithdrawal['status'], string[]> = {
  pending: ['RISK_REVIEW', 'FINANCE_REVIEW', 'SUBMITTED'],
  paying: ['BANK_PROCESSING', 'PAYOUT_PROCESSING'],
  done: ['COMPLETED'],
  rejected: ['REJECTED', 'CANCELLED'],
};

function mockWithdrawalItems(params?: WithdrawalListParams): WithdrawalApiItem[] {
  let pendingIdx = 0;
  let payingIdx = 0;
  let rejectedIdx = 0;
  const items = mockWithdrawals.map((w, i) => {
    let rawStatus: string;
    if (w.status === 'pending') {
      rawStatus = MOCK_RAW_BY_UI.pending[pendingIdx % MOCK_RAW_BY_UI.pending.length];
      pendingIdx += 1;
    } else if (w.status === 'paying') {
      rawStatus = MOCK_RAW_BY_UI.paying[payingIdx % MOCK_RAW_BY_UI.paying.length];
      payingIdx += 1;
    } else if (w.status === 'rejected') {
      rawStatus = MOCK_RAW_BY_UI.rejected[rejectedIdx % MOCK_RAW_BY_UI.rejected.length];
      rejectedIdx += 1;
    } else {
      rawStatus = 'COMPLETED';
    }
    return {
      withdrawalId: w.id.replace(/\D/g, '') || w.id,
      subjectType: MOCK_SUBJECT_CYCLE[i % MOCK_SUBJECT_CYCLE.length],
      amount: mockAmount8(w.amount),
      currency: 'USD',
      status: rawStatus,
      createdAt: w.appliedAt,
      userDisplay: w.agentName,
      payoutLast4: '0000',
      fee: '0.00000000',
      netAmount: mockAmount8(w.amount),
    };
  });

  return items.filter((item) => {
    if (params?.status && item.status.toUpperCase() !== params.status.toUpperCase()) {
      return false;
    }
    if (params?.subjectType && item.subjectType.toUpperCase() !== params.subjectType.toUpperCase()) {
      return false;
    }
    return true;
  });
}

// ---- API ----

type DepositListParams = {
  /** 服务端按商户过滤（后端 GET /deposits 支持 merchantId） */
  merchantId?: number | string;
  /** 后端精确状态过滤（DETECTED/PENDING_REVIEW/CREDITED/EXCEPTION/IGNORED/REJECTED） */
  status?: string;
  page?: number;
  pageSize?: number;
};

/** mock：把演示数据里的 number 金额补齐为 8 位小数字符串（纯字符串处理，不参与计算） */
function mockAmount8(value: number): string {
  const [whole, frac = ''] = String(value).split('.');
  return `${whole}.${(frac + '00000000').slice(0, 8)}`;
}

/** mock：后端 DepositListResult.items 形态（金额按 8 位 DECIMAL 字符串模拟 wire 口径） */
function mockDepositItems(): DepositApiItem[] {
  return mockDeposits.map((d) => ({
    depositId: d.id.replace(/\D/g, '') || d.id,
    depositNo: d.id,
    merchantId: d.merchantId,
    method: d.type === 'bank_slip' || d.type === 'wire' ? 'BANK' : 'CRYPTO',
    amount: mockAmount8(d.amount),
    currency: 'USD',
    status: d.status === 'approved' ? 'CREDITED' : d.status === 'rejected' ? 'REJECTED' : 'PENDING_REVIEW',
    channelTxId: d.method,
    createdAt: d.submittedAt,
  }));
}

/** mock：服务端 summary 形态（与 DepositAppService.summarize 同口径） */
function mockDepositSummary(all: DepositApiItem[]): DepositListSummary {
  const pendingCount = all.filter(
    (d) => d.status === 'DETECTED' || d.status === 'PENDING_REVIEW',
  ).length;
  const credited = all.filter((d) => d.status === 'CREDITED');
  const totals = new Map<string, string>();
  for (const d of credited) {
    const currency = d.creditCurrency || d.currency;
    const amount = d.creditAmount || d.amount;
    totals.set(currency, amount);
  }
  return {
    pendingCount,
    creditedThisMonthCount: credited.length,
    creditedThisMonthTotals: [...totals.entries()]
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([currency, amount]) => ({ currency, amount })),
  };
}

export async function listDeposits(params?: DepositListParams) {
  const page = params?.page ?? 1;
  // 缺省保持既有 50（其它调用方的旧窗口口径）；审核台显式传 20
  const pageSize = params?.pageSize ?? 50;
  const q = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (params?.merchantId != null && String(params.merchantId) !== '') q.set('merchantId', String(params.merchantId));
  if (params?.status) q.set('status', params.status);
  return adminGet<DepositListResponse>(
    `/deposits?${q.toString()}`,
    () => {
      const all = mockDepositItems();
      const filtered = params?.status
        ? all.filter((d) => d.status.toUpperCase() === params.status!.toUpperCase())
        : all;
      const start = (page - 1) * pageSize;
      return {
        total: filtered.length,
        items: filtered.slice(start, start + pageSize),
        // 与真后端一致：summary 仅受 merchantId 约束，不受 status/页码影响
        summary: mockDepositSummary(all),
      };
    },
  );
}

/** 充值单通过入账（敏感写：强制 X-OTP） */
export async function approveDeposit(id: string, otp: string) {
  return adminPost<null>(
    `/deposits/${id}/approve-credit`,
    {},
    () => null,
    { headers: { 'X-OTP': otp } },
  );
}

/** 充值单拒绝（敏感写：强制 X-OTP） */
export async function rejectDeposit(id: string, reason: string, otp: string) {
  return adminPost<null>(
    `/deposits/${id}/reject`,
    { reason },
    () => null,
    { headers: { 'X-OTP': otp } },
  );
}

export async function listWithdrawals(params?: WithdrawalListParams) {
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 50;
  const qs = new URLSearchParams();
  qs.set('page', String(page));
  qs.set('pageSize', String(pageSize));
  if (params?.status) {
    qs.set('status', params.status);
  }
  if (params?.subjectType) {
    qs.set('subjectType', params.subjectType);
  }
  return adminGet<{ total: number; items: WithdrawalApiItem[] }>(
    `/withdrawals?${qs.toString()}`,
    () => {
      const items = mockWithdrawalItems(params);
      // 服务端分页口径：total 为过滤后总数，items 仅当前页
      const start = (page - 1) * pageSize;
      return { total: items.length, items: items.slice(start, start + pageSize) };
    },
  );
}

/**
 * 平台审核：优先 FINANCE 阶段；若状态为 RISK_REVIEW 则走 RISK。
 * 敏感写：强制 X-OTP。
 */
export async function approveWithdrawal(id: string, rawStatus: string | undefined, otp: string) {
  const stage =
    (rawStatus || '').toUpperCase() === 'RISK_REVIEW' ||
    (rawStatus || '').toUpperCase() === 'SUBMITTED'
      ? 'RISK'
      : 'FINANCE';
  return adminPost<null>(
    `/withdrawals/${id}/review`,
    { stage, decision: 'APPROVE' },
    () => null,
    { headers: { 'X-OTP': otp } },
  );
}

/** 提现终审拒绝（敏感写：强制 X-OTP） */
export async function rejectWithdrawal(
  id: string,
  reason: string,
  rawStatus: string | undefined,
  otp: string,
) {
  const stage =
    (rawStatus || '').toUpperCase() === 'RISK_REVIEW' ||
    (rawStatus || '').toUpperCase() === 'SUBMITTED'
      ? 'RISK'
      : 'FINANCE';
  return adminPost<null>(
    `/withdrawals/${id}/review`,
    { stage, decision: 'REJECT', reason },
    () => null,
    { headers: { 'X-OTP': otp } },
  );
}

/** 单笔超龄强驳（默认 7 日；敏感写：强制 X-OTP） */
export async function forceRejectMaxAge(id: string, maxAgeDays: number | undefined, otp: string) {
  const qs = new URLSearchParams();
  if (maxAgeDays != null) {
    qs.set('maxAgeDays', String(maxAgeDays));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return adminPost<null>(
    `/withdrawals/${id}/force-reject-max-age${suffix}`,
    {},
    () => null,
    { headers: { 'X-OTP': otp } },
  );
}

/** 批量强驳任务重跑（可选；敏感写：强制 X-OTP） */
export async function retryForceRejectJob(maxAgeDays: number | undefined, otp: string) {
  const qs = new URLSearchParams();
  if (maxAgeDays != null) {
    qs.set('maxAgeDays', String(maxAgeDays));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return adminPost<null>(
    `/withdrawals/jobs/force-reject/retry${suffix}`,
    {},
    () => null,
    { headers: { 'X-OTP': otp } },
  );
}

export { USE_MOCK };

/** 平台资金总览（监管备付快照包装） GET /finance/overview */
type FinanceOverviewView = {
  asOf?: string | null;
  latestSnapshot?: {
    snapshotId?: string;
    jurisdiction?: string;
    currency?: string;
    escrowBalance?: string;
    userFundsTotal?: string;
    complianceRatio?: string;
    tier?: string;
    bankReadOk?: boolean;
    snapshotTime?: string;
    accountAvailable?: string | null;
    accountFrozen?: string | null;
    accountInTransit?: string | null;
    cardBalance?: string | null;
    commissionPending?: string | null;
    spreadPending?: string | null;
  } | null;
  policySummary?: {
    yellowWithdrawGte?: string;
    bankFailYellowMinutes?: number;
    bankFailRedMinutes?: number;
    yellowBlockTransfer?: boolean;
    defaultJurisdiction?: string;
    defaultCurrency?: string;
  } | null;
  notes?: string | null;
};

export async function getFinanceOverview(params?: { jurisdiction?: string; currency?: string }) {
  const q = new URLSearchParams();
  if (params?.jurisdiction) q.set('jurisdiction', params.jurisdiction);
  if (params?.currency) q.set('currency', params.currency);
  const qs = q.toString() ? `?${q}` : '';
  return adminGet<FinanceOverviewView>(`/finance/overview${qs}`, () => ({
    asOf: new Date().toISOString(),
    latestSnapshot: {
      snapshotId: 'mock-1',
      jurisdiction: 'DEFAULT',
      currency: 'USD',
      escrowBalance: '1025000.00',
      userFundsTotal: '1000000.00',
      complianceRatio: '102.50',
      tier: 'GREEN',
      bankReadOk: true,
      snapshotTime: new Date().toISOString(),
      accountAvailable: '800000.00',
      cardBalance: '150000.00',
      commissionPending: '12000.00',
      spreadPending: '8000.00',
    },
    policySummary: null,
    notes: 'mock',
  }));
}
